import { ChartFiltersService } from './chart-filters.service';
import { Injectable, signal, WritableSignal, computed, Signal } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { concatMap, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { ScopeService } from './scope.service';
import { UtilsService } from './utils.service';
import { ChartsService } from './charts.service';
import { PerimetersService } from './perimeters.service';
import { YearsService } from './years.service';
import { ConfigService } from './config.service';
import { PPA_OPTION_STORAGE_KEY } from './constants';

export type PriceTier = 'min' | 'med' | 'max';

export interface AutoPpaAssetMap {
  [assetId: number]: {
    replacement_asset_type_id: number;
    asset_selected: boolean;
    investment_selected: boolean;
    asset: KPI.MappedMinimalAsset;
    price_sheet_id?: number;
    selected_price_tier?: PriceTier;
    investment_type_id?: number;
  }
};

export interface AutoPPAInput {
  asset: number,
  asset_type: number,
  price_tier?: PriceTier,
}

/*
* StoredAutoPPA is a map of assetId, each entry containing these values:
*
* index 0: replacement_asset_type_id
* index 1: asset_selected
* index 2: investment_selected
* index 3: price_sheet_id (optional)
* index 4: selected_price_tier (optional: 'min' | 'med' | 'max')
* index 5: investment_type_id (optional)
*
* We store this in local storage to remember the user's selection, so we need it to be as small as possible
*/
export interface StoredAutoPPA {
  [assetId: number]: [number, boolean, boolean, number?, string?, number?]
}

@Injectable({
  providedIn: 'root',
})
export class AutoPpaService {
  constructor(
    private scopeService: ScopeService,
    private api: APIService,
    private chartsService: ChartsService,
    private yearsService: YearsService,
    private perimetersService: PerimetersService,
    private chartFiltersService: ChartFiltersService,
    private configService: ConfigService,
  ) {
    let lastPPAScope: string;
    combineLatest([
      this.perimetersService.perimeters$,
      this.yearsService.endYear$,
      this.chartFiltersService.filter$('theoreticalOrAudit', false),
    ]).pipe(
      filter(([perimeters, endYear, theoreticalOrAudit]) => {
        const scope = this.generateScopeKey(endYear, perimeters, theoreticalOrAudit);
        const pass = !!lastPPAScope && scope !== lastPPAScope;
        lastPPAScope = scope;
        return pass;
      }),
    ).subscribe(() => {
      this.resetlocalStoragePPA();
      this.resetautoPPA();
    });
  }

  private autoPpaEnabled$: BehaviorSubject<boolean>;
  public get isAutoPpaEnabled$(): Observable<boolean> {
    if (!this.autoPpaEnabled$) {
      this.autoPpaEnabled$ = new BehaviorSubject(false);
      this.api.get_decision_settings().pipe(first()).subscribe((value) =>
        this.autoPpaEnabled$.next(value.enable_ppa_automatic),
        () => this.autoPpaEnabled$.next(false),
      );
    }
    return this.autoPpaEnabled$
  }

  // Selected / Unselected assetIds for the Auto PPA
  public tmpAutoPPA$ = new BehaviorSubject<AutoPpaAssetMap>({});
  public autoPPA$ = new BehaviorSubject<AutoPpaAssetMap>({});
  public setAutoPPA(autoPPA: AutoPpaAssetMap, save: boolean): void {
    if (save) {
      this.tmpAutoPPA$.next(autoPPA);
      this.autoPPA$.next(autoPPA);
      this.savePPAToLocalStorage(autoPPA);
    } else {
      this.tmpAutoPPA$.next(autoPPA);
    }
  }

  public get ppaScope$(): Observable<string> {
    return combineLatest([
      this.yearsService.endYear$,
      this.perimetersService.perimeters$,
      this.chartFiltersService.filter$('theoreticalOrAudit', false),
    ]).pipe(
      map(([endYear, perimeters, theoreticalOrAudit]) =>
        this.generateScopeKey(endYear, perimeters, theoreticalOrAudit),
      ),
    );
  }
  private generateScopeKey(
    endYear: number,
    perimeters: KPI.Perimeter[],
    theoreticalOrAudit: string | number,
  ): string {
    return endYear + ':' + theoreticalOrAudit + ':' + PerimetersService.getBuildingsIds(perimeters)
      .sort((a, b) => a - b)
      .join(',');
  }

  private savePPAToLocalStorage(autoPpaAssets: AutoPpaAssetMap): void {
    const ppaAssetsToStore: StoredAutoPPA = Object.keys(autoPpaAssets)
      .map(Number)
      .reduce((acc, key) => {
        acc[key] = [
          autoPpaAssets[key].replacement_asset_type_id,
          autoPpaAssets[key].asset_selected,
          autoPpaAssets[key].investment_selected,
          autoPpaAssets[key].price_sheet_id || null,
          autoPpaAssets[key].selected_price_tier || null,
          autoPpaAssets[key].investment_type_id || null,
        ];
        return acc;
      }, {});

    // Save the selected assets in local storage
    this.ppaScope$.pipe(first()).subscribe((scope) => {
      localStorage.setItem('auto-ppa-assets', JSON.stringify({
        [scope]: ppaAssetsToStore,
      }));
    });
  }

  public get localStoragePPA$(): Observable<StoredAutoPPA> {
    return this.ppaScope$.pipe(
      map((scope) => {
        const stored: {[scope: string]: StoredAutoPPA} = JSON.parse(localStorage.getItem('auto-ppa-assets') || '{}');
        const storedScope = stored[scope];
        if (!storedScope) {
          this.resetlocalStoragePPA();
        }
        return storedScope || {};
      }),
    );
  }

  public resetlocalStoragePPA(): void {
    localStorage.removeItem('auto-ppa-assets');
  }

  // Trigger this if the user leaves or has finished with the Auto PPA
  public resetautoPPA(): void {
    this.changePPAStep(1);
    this.setAutoPPA({}, true);
  }

  // Get Auto PPA investment rows
  public _autoPPARows$: { [key: string]: Observable<KPI.AutoPPARow[]> } = {};
  public getAutoPPAInvestments(): Observable<KPI.AutoPPARow[]> {
    return combineLatest([
      this.autoPPA$,
      this.chartFiltersService.filter$('theoreticalOrAudit', false),
    ]).pipe(
      map(([autoPPA, eolType]) => [this.getInputsFromAutoPpaAssetMap(autoPPA, true), this.buildPriceSheetMappingsWithTier(autoPPA), eolType]),
      switchMap(([items, priceSheetMappings, eolType]) => {
        const key = UtilsService.getKey({items, priceSheetMappings, eolType});
        if (!this._autoPPARows$[key]) {
          this._autoPPARows$[key] = this.api.post_auto_investment_plan(
            true,
            items as AutoPPAInput[],
            priceSheetMappings as { asset: number; pricesheet: number; price_tier?: PriceTier }[],
            eolType as KPI.TheoreticalOrAuditFilter,
          ).pipe(shareReplay(1));
        }
        return this._autoPPARows$[key];
      }),
    )
  }

  // Execute Auto PPA
  public executeAutoPPA(): Observable<{ executed_items_nb: number; total_items_nb: number; assets: number[] }> {
    const batchSize = 20;
    return combineLatest([
      this.autoPPA$,
      this.chartFiltersService.filter$('theoreticalOrAudit', false),
    ]).pipe(
      map(([autoPPA, eolType]) => [
        this.getInputsFromAutoPpaAssetMap(autoPPA, false),
        this.buildPriceSheetMappingsWithTier(autoPPA),
        eolType
      ] as [AutoPPAInput[], { asset: number; pricesheet: number; price_tier?: PriceTier }[], KPI.TheoreticalOrAuditFilter]),
      switchMap(([items, priceSheetMappings, eolType]) => {
        const batches: AutoPPAInput[][] = this.chunkArray(items, batchSize);
        const allAssets: number[] = [];
        return from(batches).pipe(
          concatMap((batch, i) =>
            this.api.post_auto_investment_plan(
              false,
              batch,
              priceSheetMappings,
              eolType,
            ).pipe(
              tap((rows) => {
                allAssets.push(...rows.map(row => row.asset));
              }),
              map(() => ({
                executed_items_nb: Math.min(i * batchSize + batch.length, items.length),
                total_items_nb: items.length,
                assets: [...allAssets], // shallow copy to avoid mutation
              })),
              tap((res) => {
                if (res.executed_items_nb === res.total_items_nb) {
                  this.chartsService.capexHasChanged();
                }
              })
            )
          )
        );
      }),
    );
  }

  private chunkArray(array: any[], chunkSize: number): AutoPPAInput[][] {
    const results = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      results.push(array.slice(i, i + chunkSize));
    }
    return results;
  }

  private getInputsFromAutoPpaAssetMap(autoPpaAsset: AutoPpaAssetMap, preview: boolean): AutoPPAInput[] {
    return Object.keys(autoPpaAsset)
      .map(Number)
      .filter((k) => preview ? !!autoPpaAsset[k].asset_selected : autoPpaAsset[k].investment_selected)
      .sort((a, b) => a - b)
      .map((k) => ({
        asset: Number(k),
        asset_type: autoPpaAsset[k].replacement_asset_type_id,
        price_tier: autoPpaAsset[k].selected_price_tier || 'med',
      }));
  };

  public getAssetTypesReplacements(assets: KPI.MappedMinimalAsset[]): Observable<KPI.AssetTypeReplacementResponse> {
    return combineLatest([
      this.scopeService.subCategories$,
      this.scopeService.assetTypes$,
      this.api.get_asset_types_replacements(assets.map((asset) => asset.asset_type))
    ]).pipe(
      map(([subCategories, assetTypes, res]) => ({
        asset_types: res.asset_types.map((elt) => ({
          ...elt,
          asset_type_object: assetTypes[elt.asset_type],
          sub_category_object: subCategories[assetTypes[elt.asset_type].parent],
        })),
        all_choices: res.all_choices.map((choice) => ({
          ...choice,
          asset_type_object: assetTypes[choice.id],
          sub_category_object: subCategories[assetTypes[choice.id].parent],
        }),
        )
      })),
    )
  }

  private priceSheetMappings: { asset: number; pricesheet: number; price_tier?: PriceTier }[] = [];
  public updateSelectedPriceSheet(currentAssetId: number, pricesheetId: number) {
    const priceSheetMapping = { asset: currentAssetId, pricesheet: pricesheetId };
    this.sortSelectedPriceSheets(priceSheetMapping);
  }

  private sortSelectedPriceSheets(priceSheetMapping: { asset: number; pricesheet: number; price_tier?: PriceTier }) {
    const existingIndex = this.priceSheetMappings.findIndex(
      (mapping) => mapping.asset === priceSheetMapping.asset
    );
    if (priceSheetMapping.pricesheet === null) {
      if (existingIndex !== -1) {
        this.priceSheetMappings.splice(existingIndex, 1);
      }
    } else {
      if (existingIndex !== -1) {
        this.priceSheetMappings[existingIndex] = priceSheetMapping;
      } else {
        this.priceSheetMappings.push(priceSheetMapping);
      }
    }
  }

  private buildPriceSheetMappingsWithTier(autoPPA: AutoPpaAssetMap): { asset: number; pricesheet: number; price_tier?: PriceTier }[] {
    return this.priceSheetMappings.map((mapping) => ({
      ...mapping,
      price_tier: autoPPA[mapping.asset]?.selected_price_tier || 'med',
    }));
  }

  public getPriceSheetMappings(): { asset: number; pricesheet: number; price_tier?: PriceTier }[] {
    return this.priceSheetMappings;
  }

  public _autoPPAStep: WritableSignal<number> = signal(1);

  public get autoPPAStep(): Signal<number> {
    return computed(() => this._autoPPAStep());
  }

  public changePPAStep(step: number): void {
    combineLatest([
      this.autoPPA$,
      this.tmpAutoPPA$,
    ]).pipe(first()).subscribe(([autoPPA, tmpAutoPPA]) => {
      if (step === 2 && !Object.keys(autoPPA).length && !!Object.keys(tmpAutoPPA).length) {
        this.setAutoPPA(tmpAutoPPA, true);
      }
      this._autoPPAStep.set(step);
    });
  }

  public autoInvestmentSitesLimit$: Observable<number> = this.configService.customerConfig$.pipe(
    map(({auto_investment_plan_sites_limit}) => auto_investment_plan_sites_limit),
  );

  public sitesLimitExceeded$: Observable<boolean> = combineLatest([
    this.perimetersService.perimeters$,
    this.configService.customerConfig$,
  ]).pipe(
    map(([perimeters, customerConfig]) => {
      const perimetersCount = perimeters.length;
      return perimetersCount > customerConfig.auto_investment_plan_sites_limit;
    }),
  );

  private _selectedOption = signal<KPI.PPAOption>(
    Number(localStorage.getItem(PPA_OPTION_STORAGE_KEY)) || KPI.PPAOption.LAST_AUDIT
  );

  public selectedOption = this._selectedOption.asReadonly();
  setSelectedOption(option: KPI.PPAOption): void {
    this._selectedOption.set(option);
    localStorage.setItem(PPA_OPTION_STORAGE_KEY, String(option));
  }
}
